import useSWR from "swr";
import { useWsmPlugin } from "../useWsmPlugin";
import { useCallback, useEffect, useMemo } from "react";
import { LocalEntityType } from "@WahooFitness/wsm-native/dist/esm/types/local_entity_type";
import { WorkoutProfileType } from "@WahooFitness/cloud-client-types";
import { useDefaultWorkoutTypes } from "./useDefaultWorkoutTypes";
import { useConfigContext, useGuardedUpdater } from "@WahooFitness/wahoo-offline-mfe";
import { IWorkoutProfilesSource, sortWorkoutProfiles } from "./useWorkoutProfilesSource";
import { t } from "@lingui/macro";
import { debounce } from "lodash";
import { Base64 } from "js-base64";

export const useWsmWorkoutProfilesSource = (enabled: boolean): IWorkoutProfilesSource => {
  const { sensorManagerPlugin } = useWsmPlugin();
  const { defaultWorkoutTypesMap, defaultWorkoutTypesAreLoading } = useDefaultWorkoutTypes();
  const { wahooToken } = useConfigContext();
  const { update, updateInProgress } = useGuardedUpdater();

  const {
    data: workoutProfiles,
    isLoading: workoutProfilesAreLoading,
    error: workoutProfilesError,
    mutate: reloadWorkoutProfiles,
  } = useSWR(enabled && ["wsmWorkoutProfiles", wahooToken], async () => {
    const profiles = await sensorManagerPlugin.getWorkoutProfiles();
    return profiles.workoutProfiles.filter((p) => p["#deleted"] !== true).sort(sortWorkoutProfiles);
  });

  useEffect(() => {
    if (!enabled) {
      return;
    }
    const listener = sensorManagerPlugin.addListener("cloudEntityChanged", (event) => {
      if (event.entityType === LocalEntityType.WorkoutProfile) {
        reloadWorkoutProfiles();
      }
    });
    return () => {
      (async () => (await listener)?.remove())();
    };
  }, [enabled, reloadWorkoutProfiles, sensorManagerPlugin]);

  const getWorkoutProfile = useCallback(
    (workoutProfileExternalId: string) => {
      return workoutProfiles?.find((p) => p.external_id === workoutProfileExternalId);
    },
    [workoutProfiles]
  );

  const createWorkoutProfile = useCallback(
    (workoutProfile: WorkoutProfileType) => {
      return update(
        async () => {
          const newProfile = await sensorManagerPlugin.createWorkoutProfile({
            workoutProfile: { ...workoutProfile },
          });
          return newProfile.workoutProfile;
        },
        {
          errorMessage: t`Something went wrong creating the profile. Please restart the app and try again.`,
        }
      );
    },
    [sensorManagerPlugin, update]
  );

  const editWorkoutProfile = useCallback(
    (workoutProfileExternalId: string, edits: Partial<WorkoutProfileType>) => {
      return update(
        async () => {
          const { completed } = await sensorManagerPlugin.editLocalEntity({
            entityType: LocalEntityType.WorkoutProfile,
            token: workoutProfileExternalId,
            edits,
          });
          if (completed) {
            const result = (
              await sensorManagerPlugin.getWorkoutProfile({ token: workoutProfileExternalId })
            ).workoutProfile;
            return result;
          } else {
            throw new Error("Failed to edit workout profile");
          }
        },
        {
          errorMessage: t`Something went wrong editing the profile. Please restart the app and try again.`,
        }
      );
    },
    [sensorManagerPlugin, update]
  );

  const deleteWorkoutProfile = useCallback(
    (workoutProfileExternalId: string) => {
      return update(
        async () => {
          const { completed } = await sensorManagerPlugin.editLocalEntity({
            entityType: LocalEntityType.WorkoutProfile,
            token: workoutProfileExternalId,
            edits: { "#deleted": true },
          });
          if (!completed) {
            throw new Error("Failed to delete workout profile");
          }
          return completed;
        },
        {
          errorMessage: t`Something went wrong deleting the profile. Please restart the app and try again.`,
        }
      );
    },
    [sensorManagerPlugin, update]
  );

  const resetWorkoutProfile = useCallback(
    (workoutProfileExternalId: string) => {
      return update(
        async () => {
          if (defaultWorkoutTypesAreLoading || !defaultWorkoutTypesMap) {
            throw new Error("Default workout types are still loading");
          }
          const currentProfile = await sensorManagerPlugin.getWorkoutProfile({
            token: workoutProfileExternalId,
          });
          if (currentProfile?.workoutProfile?.workout_type_id === undefined) {
            throw new Error("Workout profile not found");
          }
          const defaultProfile =
            defaultWorkoutTypesMap[currentProfile.workoutProfile.workout_type_id];
          const result = await editWorkoutProfile(workoutProfileExternalId, defaultProfile);
          return result;
        },
        {
          errorMessage: t`Something went wrong resetting the profile. Please restart the app and try again.`,
        }
      );
    },
    [
      defaultWorkoutTypesAreLoading,
      defaultWorkoutTypesMap,
      editWorkoutProfile,
      sensorManagerPlugin,
      update,
    ]
  );

  const updateWorkoutProfilePagesImmediately = useCallback(
    (workoutProfileExternalId: string, pages: Uint8Array) => {
      return update(
        async () => {
          const { completed } = await sensorManagerPlugin.editLocalEntity({
            entityType: LocalEntityType.WorkoutProfile,
            token: workoutProfileExternalId,
            edits: { "#display_cfg": Base64.fromUint8Array(pages) },
          });
          if (!completed) {
            throw new Error("Failed to edit workout profile pages");
          }
        },
        {
          errorMessage: t`Something went wrong updating the profile pages. Please restart the app and try again.`,
        }
      );
    },
    [sensorManagerPlugin, update]
  );

  // Since we are saving locally, we don't need to throttle.
  const updateWorkoutProfilePagesDebounce = useMemo(
    () => debounce(updateWorkoutProfilePagesImmediately, 0),
    [updateWorkoutProfilePagesImmediately]
  );

  const getWorkoutProfilePages = useCallback(
    async (workoutProfileExternalId: string) => {
      const workoutProfile = getWorkoutProfile(workoutProfileExternalId);
      if (!workoutProfile?.["#display_cfg"]) {
        return;
      }
      return Base64.toUint8Array(workoutProfile["#display_cfg"]);
    },
    [getWorkoutProfile]
  );

  return {
    workoutProfiles,
    workoutProfilesError,
    workoutProfilesAreLoading,
    workoutProfileUpdateInProgress: updateInProgress,
    reloadWorkoutProfiles,
    getWorkoutProfile,
    createWorkoutProfile,
    editWorkoutProfile,
    deleteWorkoutProfile,
    resetWorkoutProfile,
    updateWorkoutProfilePagesImmediately,
    updateWorkoutProfilePagesDebounce,
    setIgnoreWorkoutProfileUpdates: () => {}, // Not needed for WSM
    getWorkoutProfilePages,
  };
};
